/**
 * SearchPriorityDomainsPanel — Block S1 (Search v2 foundation).
 *
 * List of hostnames (one per chip, free-text add). Pipeline start payload
 * includes them as `priority_domains` → orchestrator builds `site:` queries
 * primary + general fallback (spec § B3, § B4).
 *
 * The chip is also reflected in LibraryBar snapshots (search snapshot v1
 * already supports the key, see src/store/librarySnapshots.ts).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe2, Plus, X } from 'lucide-react';

import { useSearchStore } from '@/store/searchStore';
import { cn } from '@/lib/utils';

const HOST_RE = /^[a-z0-9]([a-z0-9-]*\.)+[a-z]{2,}$/i;

function _normaliseHost(raw: string): string | null {
  const v = raw.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0];
  if (!v || v === 'localhost') return null;
  if (HOST_RE.test(v)) return v;
  return null;
}

export function SearchPriorityDomainsPanel() {
  const { t } = useTranslation();
  const domains = useSearchStore((s) => s.priorityDomains);
  const addPriorityDomain = useSearchStore((s) => s.addPriorityDomain);
  const removePriorityDomain = useSearchStore((s) => s.removePriorityDomain);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleAdd() {
    // P1.2: comma/whitespace-split — paste or type "a.com, b.com" + Enter →
    // all valid hosts added at once; invalid ones reported in one message.
    const parts = draft
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const p of parts) {
      const n = _normaliseHost(p);
      if (n) valid.push(n);
      else invalid.push(p);
    }
    for (const v of valid) addPriorityDomain(v);
    if (invalid.length > 0) {
      setError(
        t('search.priorityDomains.invalidBatch', {
          defaultValue: 'Invalid hosts: {{hosts}}',
          hosts: invalid.join(', '),
        }),
      );
    } else {
      setError(null);
    }
    setDraft('');
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center gap-2">
        <Globe2 className="w-4 h-4 text-surface-400" />
        <span className="text-sm font-semibold">
          {t('search.priorityDomains.title', { defaultValue: 'Priority domains' })}
        </span>
        <span className="text-xs text-surface-500 ml-2">
          {t('search.priorityDomains.help', {
            defaultValue: 'One host per line or comma. The system searches these domains first.',
          })}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {domains.map((d) => (
          <span
            key={d}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-500/10 border border-primary-500/30 text-primary-200 text-xs"
          >
            {d}
            <button
              type="button"
              onClick={() => removePriorityDomain(d)}
              className="ml-1 text-primary-300/80 hover:text-primary-300"
              aria-label={t('search.priorityDomains.remove', { defaultValue: 'Remove' })}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {domains.length === 0 && (
          <span className="text-xs text-surface-500">
            {t('search.priorityDomains.empty', { defaultValue: 'No domains yet — add a brand site.' })}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          className={cn('input flex-1', error && 'border-danger-tint/50')}
          placeholder={t('search.priorityDomains.placeholder', { defaultValue: 'adidas.com.ua' })}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <button type="button" className="btn-secondary inline-flex items-center gap-1.5" onClick={handleAdd}>
          <Plus className="w-3.5 h-3.5" />
          {t('search.priorityDomains.add', { defaultValue: 'Add' })}
        </button>
      </div>
      {error && <div className="text-xs text-danger">{error}</div>}
    </div>
  );
}
